/**
 * Favorites Query Hooks
 * 
 * TanStack Query-based data fetching for favorites with:
 * - Global prefetching at app load
 * - Stale-while-revalidate pattern
 * - Optimistic updates for instant UI feedback
 * - Background revalidation
 */

import { useEffect, useCallback } from 'react';
import { 
  useQuery, 
  useMutation, 
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { queryKeys } from '../lib/queryClient';
import { Apartment, ApartmentWithStatus, Match, DBMeeting } from '../types';
import { getMeetingsForRenter } from '../lib/meetings';
import { getPartnershipForApartment, isPartnershipAccepted } from '../lib/partnerships';
import { getCachedPartnership, setCachedPartnership } from '../lib/partnershipCache';

/**
 * Core fetch function for favorites data
 * Extracted to be reusable by both query and prefetch
 */
async function fetchFavoritesData(userId: string): Promise<ApartmentWithStatus[]> {
  // Fetch liked apartments with only needed columns
  const { data: matchesData, error: matchesError } = await supabase
    .from('matches')
    .select(`
      apartment_id,
      apartment:apartments(
        id, title, price, address, neighborhood, city,
        rooms, sqm, images, amenities, owner_id, listing_type
      )
    `)
    .eq('renter_id', userId);

  if (matchesError) throw matchesError;

  // Fetch scheduled meetings
  const meetingsData = await getMeetingsForRenter(userId);

  // Create a Map keyed by apartment ID for deduplication
  const apartmentMap = new Map<string, ApartmentWithStatus>();

  // Add all liked apartments
  const likedApartments = (matchesData || [])
    .map((match: Pick<Match, 'apartment_id' | 'apartment'>) => match.apartment)
    .filter((apt): apt is Apartment => apt !== null);

  likedApartments.forEach(apt => {
    apartmentMap.set(apt.id, {
      ...apt,
      isLiked: true,
      isScheduled: false,
    });
  });

  // Group meetings by apartment and find the best one to display
  // Prioritize: accepted > pending, then earliest time
  const apartmentMeetingsMap = new Map<string, DBMeeting & { apartment: Apartment | null }>();

  meetingsData.forEach((meeting: DBMeeting & { apartment: Apartment | null }) => {
    const apartment = meeting.apartment;
    if (!apartment) return;
    
    // Skip declined meetings - they shouldn't show as scheduled
    if (meeting.status === 'declined') return;
    
    const existing = apartmentMeetingsMap.get(apartment.id);
    
    // If no existing meeting for this apartment, add it
    if (!existing) {
      apartmentMeetingsMap.set(apartment.id, meeting);
      return;
    }
    
    // Prioritize: accepted > pending, then earliest time
    const existingTime = new Date(existing.start_time).getTime();
    const currentTime = new Date(meeting.start_time).getTime();
    
    // If current is accepted and existing is pending, prefer current
    if (meeting.status === 'accepted' && existing.status === 'pending') {
      apartmentMeetingsMap.set(apartment.id, meeting);
      return;
    }
    
    // If both same status, prefer earlier time
    if (meeting.status === existing.status && currentTime < existingTime) {
      apartmentMeetingsMap.set(apartment.id, meeting);
      return;
    }
    
    // Otherwise keep existing
  });

  // Now process the selected meetings
  apartmentMeetingsMap.forEach((meeting, apartmentId) => {
    const apartment = meeting.apartment;
    if (!apartment) return;
    
    const startTime = new Date(meeting.start_time);
    const upcomingMeeting = {
      id: meeting.id,
      date: startTime.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
      time: startTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
      startTime: startTime,
      status: meeting.status as 'pending' | 'accepted',
    };

    if (apartmentMap.has(apartmentId)) {
      // Update existing entry
      const existing = apartmentMap.get(apartmentId)!;
      apartmentMap.set(apartmentId, {
        ...existing,
        isScheduled: true,
        upcomingMeeting,
      });
    } else {
      // Add new entry
      apartmentMap.set(apartmentId, {
        ...apartment,
        isLiked: false,
        isScheduled: true,
        upcomingMeeting,
      });
    }
  });

  // Convert map to array
  const mergedApartments = Array.from(apartmentMap.values());
  
  // Fetch partnerships for all apartments in parallel, using cache when available
  const apartmentsWithPartners = await Promise.all(
    mergedApartments.map(async (apt) => {
      // Check cache first
      const cached = getCachedPartnership(userId, apt.id);
      if (cached) {
        return {
          ...apt,
          hasPartner: cached.hasPartner,
        };
      }
      
      // Not in cache - fetch from DB
      const partnership = await getPartnershipForApartment(apt.id);
      const hasPartner = partnership ? isPartnershipAccepted(partnership) : false;
      
      // Cache the result
      setCachedPartnership(userId, apt.id, partnership, hasPartner);
      
      return {
        ...apt,
        hasPartner,
      };
    })
  );
  
  // Sort by priority: 1. Scheduled + Liked, 2. Scheduled only, 3. Liked only
  apartmentsWithPartners.sort((a, b) => {
    const aPriority = (a.isScheduled && a.isLiked) ? 1 : (a.isScheduled ? 2 : 3);
    const bPriority = (b.isScheduled && b.isLiked) ? 1 : (b.isScheduled ? 2 : 3);
    
    if (aPriority !== bPriority) {
      return aPriority - bPriority;
    }
    
    // Within same priority, sort by meeting date (if scheduled) or alphabetically
    if (a.isScheduled && b.isScheduled && a.upcomingMeeting && b.upcomingMeeting) {
      return a.upcomingMeeting.startTime.getTime() - b.upcomingMeeting.startTime.getTime();
    }
    
    return a.title.localeCompare(b.title);
  });
  
  return apartmentsWithPartners;
}

/**
 * Custom hook to get current user ID
 * Returns null if not authenticated
 */
function useCurrentUserId() {
  const { data: userId } = useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user?.id ?? null;
    },
    staleTime: Infinity, // User ID doesn't change during session
    gcTime: Infinity,
  });
  return userId ?? null;
}

/**
 * Main favorites query hook
 * 
 * Features:
 * - Automatic caching and deduplication
 * - Background revalidation when stale
 * - Keeps previous data during refetch (no flash)
 */
export function useFavorites() {
  const userId = useCurrentUserId();
  
  return useQuery({
    queryKey: queryKeys.favorites.byUser(userId ?? 'anonymous'),
    queryFn: () => fetchFavoritesData(userId!),
    enabled: !!userId, // Only fetch when we have a user
    placeholderData: keepPreviousData, // Prevents flash during revalidation
    staleTime: 30 * 1000, // Consider data fresh for 30 seconds
  });
}

/**
 * Prefetch favorites at app load
 * Call this in AuthenticatedLayout to warm the cache before user navigates
 * Returns a function to manually trigger prefetch
 */
export function usePrefetchFavorites() {
  const queryClient = useQueryClient();
  
  const prefetch = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    
    // Prefetch favorites data into the cache
    await queryClient.prefetchQuery({
      queryKey: queryKeys.favorites.byUser(user.id),
      queryFn: () => fetchFavoritesData(user.id),
      staleTime: 30 * 1000,
    });
  }, [queryClient]);
  
  return prefetch;
}

/**
 * Mutation hook for unfavoriting an apartment
 * 
 * Features:
 * - Optimistic update - UI changes instantly
 * - Automatic rollback on error
 * - Cache invalidation on success
 */
export function useUnfavoriteMutation() {
  const queryClient = useQueryClient();
  const userId = useCurrentUserId();
  
  return useMutation({
    mutationFn: async (apartmentId: string) => {
      const { error } = await supabase
        .from('matches')
        .delete()
        .eq('renter_id', userId)
        .eq('apartment_id', apartmentId);
      
      if (error) throw error;
      return apartmentId;
    },
    
    // Optimistic update - instant UI feedback
    onMutate: async (apartmentId: string) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ 
        queryKey: queryKeys.favorites.byUser(userId ?? 'anonymous') 
      });
      
      // Snapshot the previous value
      const previousFavorites = queryClient.getQueryData<ApartmentWithStatus[]>(
        queryKeys.favorites.byUser(userId ?? 'anonymous')
      );
      
      // Optimistically update: remove or mark as not liked
      queryClient.setQueryData<ApartmentWithStatus[]>(
        queryKeys.favorites.byUser(userId ?? 'anonymous'),
        (old) => {
          if (!old) return [];
          return old
            .map((apt) => {
              if (apt.id === apartmentId) {
                // If scheduled, keep but mark as not liked
                if (apt.isScheduled) {
                  return { ...apt, isLiked: false };
                }
                // Will be filtered out
                return apt;
              }
              return apt;
            })
            .filter((apt) => apt.id !== apartmentId ? true : apt.isScheduled);
        }
      );
      
      // Return context for rollback
      return { previousFavorites };
    },
    
    // Rollback on error
    onError: (_err, _apartmentId, context) => {
      if (context?.previousFavorites) {
        queryClient.setQueryData(
          queryKeys.favorites.byUser(userId ?? 'anonymous'),
          context.previousFavorites
        );
      }
    },
    
    // Always refetch after mutation settles
    onSettled: () => {
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.favorites.byUser(userId ?? 'anonymous') 
      });
    },
  });
}

/**
 * Mutation hook for adding a favorite
 * 
 * Features:
 * - Optimistic update - UI changes instantly
 * - Automatic rollback on error
 */
export function useFavoriteMutation() {
  const queryClient = useQueryClient();
  const userId = useCurrentUserId();
  
  return useMutation({
    mutationFn: async (apartmentId: string) => {
      const { error } = await supabase
        .from('matches')
        .upsert({
          renter_id: userId,
          apartment_id: apartmentId,
        }, {
          onConflict: 'renter_id,apartment_id',
          ignoreDuplicates: true
        });
      
      // Ignore duplicate errors (23505) since it means it's already liked
      if (error && error.code !== '23505') {
        throw error;
      }
      return apartmentId;
    },
    
    // Optimistic update
    onMutate: async (apartmentId: string) => {
      await queryClient.cancelQueries({ 
        queryKey: queryKeys.favorites.byUser(userId ?? 'anonymous') 
      });
      
      const previousFavorites = queryClient.getQueryData<ApartmentWithStatus[]>(
        queryKeys.favorites.byUser(userId ?? 'anonymous')
      );
      
      // Optimistically mark as liked
      queryClient.setQueryData<ApartmentWithStatus[]>(
        queryKeys.favorites.byUser(userId ?? 'anonymous'),
        (old) => {
          if (!old) return [];
          return old.map((apt) => 
            apt.id === apartmentId ? { ...apt, isLiked: true } : apt
          );
        }
      );
      
      return { previousFavorites };
    },
    
    onError: (_err, _apartmentId, context) => {
      if (context?.previousFavorites) {
        queryClient.setQueryData(
          queryKeys.favorites.byUser(userId ?? 'anonymous'),
          context.previousFavorites
        );
      }
    },
    
    onSettled: () => {
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.favorites.byUser(userId ?? 'anonymous') 
      });
    },
  });
}

/**
 * Hook to invalidate favorites cache
 * Call after mutations that affect favorites (e.g., scheduling a tour)
 */
export function useInvalidateFavorites() {
  const queryClient = useQueryClient();
  const userId = useCurrentUserId();
  
  return () => {
    if (userId) {
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.favorites.byUser(userId) 
      });
    }
  };
}
